import { motion } from "framer-motion";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Loader2,
  AlertCircle,
  Users as UsersIcon,
  Mail,
  Shield,
  Calendar,
} from "lucide-react";

import {
  useUsers,
  useDeleteUser,
  useRoles,
  type User,
} from "../../../services/userService";
import type { RootState } from "../../../store";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import UserCreateModal from "../../../components/admin/UserCreateModal";
import UserEditModal from "../../../components/admin/UserEditModal";

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  const queryParams = {
    per_page: 10,
    page: currentPage,
    ...(searchTerm && { search: searchTerm }),
    ...(roleFilter && { role: roleFilter }),
  };

  const { data: usersData, isLoading, error } = useUsers(queryParams);
  const { data: roles } = useRoles();
  const deleteUserMutation = useDeleteUser();

  const users = usersData?.data || [];
  const pagination = usersData
    ? {
        current_page: usersData.current_page,
        last_page: usersData.last_page,
        per_page: usersData.per_page,
        total: usersData.total,
      }
    : null;

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUserMutation.mutateAsync(userToDelete.id);
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Failed to delete user:", error);
    }
  };

  const cancelDeleteUser = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleEditUser = (user: User) => {
    setUserToEdit(user);
    setIsEditModalOpen(true);
  };

  const handleCreateUser = () => {
    setIsCreateModalOpen(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString(currentLanguage, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRoleBadges = (userRoles?: Array<{ name: string } | string>) => {
    if (!userRoles || userRoles.length === 0) return null;
    return userRoles.map((role, index) => {
      const roleName = typeof role === "string" ? role : role.name;
      return (
        <Badge
          key={index}
          variant="secondary"
          className="mr-1 mb-1"
        >
          {roleName}
        </Badge>
      );
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <Card className="border-red-200 dark:border-red-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                <p>
                  {t("admin.users.error.loading", "Failed to load users. Please try again.")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <UsersIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              {t("admin.users.title", "Users")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.users.subtitle", "Manage users and their roles")}
            </p>
          </div>
          <Button
            onClick={handleCreateUser}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("admin.users.create", "Create User")}
          </Button>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder={t("admin.users.search.placeholder", "Search by name or email...")}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 w-full sm:w-[200px]"
            >
              <option value="">{t("admin.users.filter.all", "All Roles")}</option>
              {roles?.map((role) => (
                <option key={role.id} value={role.name}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Users List */}
        {isLoading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 dark:text-blue-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                {t("admin.users.loading", "Loading users...")}
              </p>
            </CardContent>
          </Card>
        ) : users.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <UsersIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                {t("admin.users.empty", "No users found")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-4">
              {users.map((user) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {user.name}
                            </h3>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Mail className="h-4 w-4" />
                              <span>{user.email}</span>
                            </div>
                            {user.created_at && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>
                                  {t("admin.users.created", "Created")}: {formatDate(user.created_at)}
                                </span>
                              </div>
                            )}
                          </div>
                          {user.roles && user.roles.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <Shield className="h-4 w-4 text-gray-400" />
                              {getRoleBadges(user.roles)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            {t("admin.users.edit", "Edit")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex justify-center items-center gap-2"
              >
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  {t("admin.users.pagination.previous", "Previous")}
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t("admin.users.pagination.page", "Page")} {pagination.current_page} {t("admin.users.pagination.of", "of")} {pagination.last_page}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((prev) => Math.min(pagination.last_page, prev + 1))}
                  disabled={currentPage === pagination.last_page}
                >
                  {t("admin.users.pagination.next", "Next")}
                </Button>
              </motion.div>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("admin.users.delete.title", "Delete User")}
              </DialogTitle>
              <DialogDescription>
                {t("admin.users.delete.description", "Are you sure you want to delete")} {userToDelete?.name}? {t("admin.users.delete.warning", "This action cannot be undone.")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cancelDeleteUser}>
                {t("admin.users.delete.cancel", "Cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteUser}
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("admin.users.delete.deleting", "Deleting...")}
                  </>
                ) : (
                  t("admin.users.delete.confirm", "Delete")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create User Modal */}
        <UserCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
          }}
        />

        {/* Edit User Modal */}
        {userToEdit && (
          <UserEditModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setUserToEdit(null);
            }}
            onSuccess={() => {
              setIsEditModalOpen(false);
              setUserToEdit(null);
            }}
            user={userToEdit}
          />
        )}
      </div>
    </div>
  );
};

export default Users;

